import { useEffect, useMemo, useState } from 'react';
import {
    FiArrowLeft,
    FiArrowRight,
    FiCheck,
    FiClipboard,
    FiCopy,
    FiFileText,
    FiServer,
    FiUploadCloud,
    FiX,
} from 'react-icons/fi';

const formatPrice = (price, currency = 'ARS') => {
    try {
        return new Intl.NumberFormat('es-AR', {
            style: 'currency',
            currency: currency || 'ARS',
            maximumFractionDigits: Number(price) % 1 === 0 ? 0 : 2,
        }).format(Number(price || 0));
    } catch {
        return `${currency || 'ARS'} ${Number(price || 0).toLocaleString('es-AR')}`;
    }
};

export const SalePurchaseModal = ({ project, onClose, onSubmit }) => {
    const [step, setStep] = useState(1);
    const [deliveryOption, setDeliveryOption] = useState('delivery');
    const [paymentCopied, setPaymentCopied] = useState(false);
    const [buyer, setBuyer] = useState({ name: '', email: '', contact: '', note: '' });
    const [receipt, setReceipt] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [result, setResult] = useState(null);

    const total = useMemo(() => formatPrice(project?.price, project?.currency), [project]);
    const deliveryOptions = useMemo(() => [
        {
            id: 'delivery',
            icon: <FiFileText />,
            title: 'Quiero recibir la entrega',
            text: project?.delivery_text || 'Revisás el pago y me enviás la demo/entrega para ponerla en marcha.',
        },
        {
            id: 'deployment',
            icon: <FiServer />,
            title: 'Necesito ayuda con el deploy',
            text: project?.support_text || 'Después de validar el pago coordinamos para dejarlo publicado.',
        },
        {
            id: 'unsure',
            icon: <FiClipboard />,
            title: 'Prefiero coordinarlo',
            text: 'Te cuento mi caso y definimos juntos la mejor forma de entrega.',
        },
    ], [project]);

    useEffect(() => {
        const html = document.documentElement;
        const body = document.body;
        const onKey = (event) => event.key === 'Escape' && onClose();
        html.classList.add('PB__modal_open');
        body.classList.add('PB__modal_open');
        window.addEventListener('keydown', onKey);
        return () => {
            html.classList.remove('PB__modal_open');
            body.classList.remove('PB__modal_open');
            window.removeEventListener('keydown', onKey);
        };
    }, [onClose]);

    const copyAlias = async () => {
        try {
            await navigator.clipboard.writeText(project?.payment_alias || '');
            setPaymentCopied(true);
            window.setTimeout(() => setPaymentCopied(false), 1800);
        } catch {
            setPaymentCopied(false);
        }
    };

    const canSubmit = buyer.name.trim()
        && (buyer.email.trim() || buyer.contact.trim())
        && receipt;

    const submit = async (event) => {
        event.preventDefault();
        if (!canSubmit || isSubmitting) return;
        setIsSubmitting(true);
        setError('');
        const body = new FormData();
        body.append('project_id', project.id);
        body.append('delivery_option', deliveryOption);
        body.append('buyer_name', buyer.name.trim());
        body.append('buyer_email', buyer.email.trim());
        body.append('buyer_contact', buyer.contact.trim());
        body.append('buyer_note', buyer.note.trim());
        body.append('receipt', receipt);
        try {
            const response = await onSubmit(body);
            setResult(response);
        } catch (currentError) {
            setError(currentError?.message || 'No se pudo enviar el comprobante.');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (result) {
        return (
            <div className="PB__sale_modal_backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
                <section className="PB__sale_modal PB__sale_modal--success" role="dialog" aria-modal="true" aria-label="Compra recibida">
                    <button type="button" className="PB__sale_modal_close" onClick={onClose} aria-label="Cerrar"><FiX /></button>
                    <span className="PB__sale_success_icon"><FiCheck /></span>
                    <div className="PB__sale_success_kicker">Comprobante recibido</div>
                    <h2>Listo, ahora lo reviso yo.</h2>
                    <p>Tu solicitud quedó registrada. Voy a validar el comprobante y contactarte para enviarte la demo/entrega o coordinar el deploy, según lo que elegiste.</p>
                    <div className="PB__sale_order_code"><span>Código de seguimiento</span><strong>{result.order_code}</strong></div>
                    <button type="button" className="PB__sale_primary" onClick={onClose}>Entendido</button>
                </section>
            </div>
        );
    }

    return (
        <div className="PB__sale_modal_backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
            <section className="PB__sale_modal" role="dialog" aria-modal="true" aria-label={`Comprar ${project.title}`}>
                <button type="button" className="PB__sale_modal_close" onClick={onClose} aria-label="Cerrar"><FiX /></button>

                <header className="PB__sale_modal_header">
                    <div>
                        <span>Compra directa</span>
                        <h2>{project.title}</h2>
                    </div>
                    <strong>{total}</strong>
                </header>

                <div className="PB__sale_steps" aria-hidden="true">
                    {[1, 2, 3].map((item) => <span key={item} className={item <= step ? 'is-active' : ''}>{item}</span>)}
                </div>

                {step === 1 && (
                    <div className="PB__sale_step_panel">
                        <div className="PB__sale_step_heading">
                            <span>Paso 1</span>
                            <h3>¿Cómo querés recibirlo?</h3>
                            <p>Esto no cambia el precio. Me sirve para saber cómo ayudarte después del pago.</p>
                        </div>
                        <div className="PB__sale_delivery_options">
                            {deliveryOptions.map((option) => (
                                <button
                                    key={option.id}
                                    type="button"
                                    className={deliveryOption === option.id ? 'is-selected' : ''}
                                    onClick={() => setDeliveryOption(option.id)}
                                >
                                    <span>{option.icon}</span>
                                    <strong>{option.title}</strong>
                                    <small>{option.text}</small>
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {step === 2 && (
                    <div className="PB__sale_step_panel">
                        <div className="PB__sale_step_heading">
                            <span>Paso 2</span>
                            <h3>Realizá la transferencia</h3>
                            <p>Transferí el importe indicado. El pago no se aprueba automáticamente: primero voy a revisar tu comprobante.</p>
                        </div>

                        <div className="PB__sale_payment_box">
                            <div className="PB__sale_payment_total"><span>Total</span><strong>{total}</strong></div>
                            <div className="PB__sale_alias_box">
                                <span>Alias</span>
                                <strong>{project.payment_alias}</strong>
                                <button type="button" onClick={copyAlias}>{paymentCopied ? <FiCheck /> : <FiCopy />} {paymentCopied ? 'Copiado' : 'Copiar alias'}</button>
                            </div>
                            {(project.payment_holder || project.payment_provider) && (
                                <div className="PB__sale_payment_meta">
                                    {project.payment_holder && <span><b>Titular</b>{project.payment_holder}</span>}
                                    {project.payment_provider && <span><b>Medio</b>{project.payment_provider}</span>}
                                </div>
                            )}
                        </div>

                        <div className="PB__sale_payment_notice">
                            Cuando termines, seguí al último paso y adjuntá el comprobante. No envíes claves, PIN ni datos de acceso bancario.
                        </div>
                    </div>
                )}

                {step === 3 && (
                    <form className="PB__sale_step_panel" onSubmit={submit}>
                        <div className="PB__sale_step_heading">
                            <span>Paso 3</span>
                            <h3>Mandame el comprobante</h3>
                            <p>Con estos datos puedo validar el pago y contactarte para la entrega.</p>
                        </div>

                        <div className="PB__sale_form_grid">
                            <label><span>Nombre *</span><input value={buyer.name} onChange={(e) => setBuyer((current) => ({ ...current, name: e.target.value }))} placeholder="Tu nombre" /></label>
                            <label><span>Email</span><input type="email" value={buyer.email} onChange={(e) => setBuyer((current) => ({ ...current, email: e.target.value }))} placeholder="nombre@email.com" /></label>
                            <label className="PB__sale_field_wide"><span>WhatsApp / contacto</span><input value={buyer.contact} onChange={(e) => setBuyer((current) => ({ ...current, contact: e.target.value }))} placeholder="Ej. +54 9 221..." /></label>
                            <label className="PB__sale_field_wide"><span>Comentario opcional</span><textarea rows="3" value={buyer.note} onChange={(e) => setBuyer((current) => ({ ...current, note: e.target.value }))} placeholder="Contame si necesitás deploy, dominio, hosting o alguna adaptación." /></label>
                            <label className="PB__sale_receipt_field PB__sale_field_wide">
                                <span><FiUploadCloud /> Comprobante *</span>
                                <input type="file" accept="image/jpeg,image/png,image/webp,application/pdf" onChange={(e) => setReceipt(e.target.files?.[0] || null)} />
                                <small>{receipt ? receipt.name : 'JPG, PNG, WEBP o PDF · máximo 8 MB'}</small>
                            </label>
                        </div>

                        <div className="PB__sale_contact_hint">Necesito al menos un email o WhatsApp para poder contactarte.</div>
                        {error && <div className="PB__sale_form_error">{error}</div>}
                    </form>
                )}

                <footer className="PB__sale_modal_footer">
                    {step > 1 ? <button type="button" className="PB__sale_back" onClick={() => setStep((current) => current - 1)}><FiArrowLeft /> Volver</button> : <span />}
                    {step < 3 ? (
                        <button type="button" className="PB__sale_primary" onClick={() => setStep((current) => current + 1)}>Continuar <FiArrowRight /></button>
                    ) : (
                        <button type="button" className="PB__sale_primary" disabled={!canSubmit || isSubmitting} onClick={submit}>
                            {isSubmitting ? 'Enviando...' : <>Enviar comprobante <FiCheck /></>}
                        </button>
                    )}
                </footer>
            </section>
        </div>
    );
};
